// src/pages/DoctorEscalationsPage.jsx
// Route: /doctor/escalations
//
// The doctor's working queue of CRITICAL escalations — acknowledge, resolve,
// snooze, assign to a navigator, suggest a specialist. It used to be the last
// panel on the old Analytics page; it is its own page (and sidebar item) so the
// queue is one click from anywhere.
//
// EscalationQueue fetches its own rows and reloads itself after every action.
// The overdue count in the header is a separate light fetch of the open list,
// refreshed through the queue's onChanged callback.

import React, { useState, useEffect, useCallback } from 'react';
import { AppShell } from '../components/Layout';
import EscalationQueue from '../components/EscalationQueue';
import OpenNotesList, { PRIORITY_OPTIONS } from '../components/OpenNotesList';
import ListSearch from '../components/ListSearch';
import api from '../services/api';

// The Constitution §III: a CRITICAL escalation must be acknowledged within 2h.
// Mirrors CriticalEscalationService.SLA_HOURS and useNotifications' copy.
const SLA_HOURS = 2;

function isSlaBreached(escalation) {
  if (escalation.status === 'ACKNOWLEDGED') return false;
  if (escalation.status === 'ESCALATED') return true;
  if (!escalation.notifiedAt) return false;
  return (Date.now() - new Date(escalation.notifiedAt).getTime()) / 3_600_000 >= SLA_HOURS;
}

export default function DoctorEscalationsPage() {
  // null = unknown (still loading, or the count could not be fetched): the
  // badge is simply omitted rather than showing a wrong zero.
  const [overdue, setOverdue] = useState(null);
  const [tab, setTab] = useState('escalations'); // escalations | notes
  const [noteCount, setNoteCount] = useState(null);
  const [query, setQuery] = useState('');
  const [priority, setPriority] = useState('ALL'); // notes tab only

  useEffect(() => {
    document.title = 'Recommendation and Escalations — Previu';
    return () => { document.title = 'Previu — Patient Management'; };
  }, []);

  const loadOverdue = useCallback(async () => {
    try {
      const res = await api.get('/doctor/critical-escalations');
      const rows = Array.isArray(res.data) ? res.data : [];
      setOverdue(rows.filter(e => e.status !== 'ACKNOWLEDGED').filter(isSlaBreached).length);
    } catch {
      setOverdue(null);
    }
  }, []);

  useEffect(() => { loadOverdue(); }, [loadOverdue]);

  return (
    <AppShell breadcrumbs={[{ label: 'Recommendation and Escalations' }]}>
      <div className={`page-header fade-in-up${tab === 'notes' ? ' escalations-header--notes' : ''}`}>
        <div>
          <div className="page-title">Recommendation and Escalations</div>
          <div className="page-subtitle">
            Critical results that need a response within {SLA_HOURS} hours.
          </div>
        </div>
      </div>

      <div className="tabs-bar fade-in-up delay-1" role="tablist" aria-label="Recommendation and Escalations">
        <button type="button" role="tab" aria-selected={tab === 'escalations'}
          className={`tab-btn${tab === 'escalations' ? ' active' : ''}`}
          onClick={() => setTab('escalations')}>
          Escalations
        </button>
        <button type="button" role="tab" aria-selected={tab === 'notes'}
          className={`tab-btn${tab === 'notes' ? ' active' : ''}`}
          onClick={() => setTab('notes')}>
          Notes{noteCount != null ? ` (${noteCount})` : ''}
        </button>
      </div>

      <ListSearch id="esc-search" label="Search recommendations and escalations"
        placeholder={tab === 'notes' ? 'Search notes by patient or description' : 'Search escalations by patient or alert'}
        value={query} onChange={setQuery}
        filters={tab === 'notes' && (
          <select className="form-select" aria-label="Filter notes by priority"
            value={priority} onChange={e => setPriority(e.target.value)}>
            <option value="ALL">All priorities</option>
            {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )} />

      {tab === 'escalations' ? (
        <section className="card fade-in-up delay-1">
          <div className="card-title">
            Escalations
            {overdue > 0 && <span className="badge badge-critical">{overdue} overdue</span>}
          </div>
          <EscalationQueue mode="doctor" onChanged={loadOverdue} query={query} />
        </section>
      ) : (
        <section className="card fade-in-up delay-1 notes-panel">
          <div className="card-title">Notes</div>
          <OpenNotesList
            patientPath={id => `/doctor/patients/${id}`}
            onLoaded={setNoteCount}
            query={query}
            priority={priority}
          />
        </section>
      )}
    </AppShell>
  );
}
